import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { addSubCategory } from "../../services/subcategory.service";
import { useSelector } from "react-redux";
import { getAllMainCategories } from "../../services/category.service";
import Select from "react-select";

export default function SubCategoryAdd() {
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const { token } = useSelector((state) => state?.auth);
  const navigate = useNavigate();
  const [mainCategories, setMainCategories] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    setValue,
    clearErrors,
  } = useForm();

  const fetchMainCategories = async () => {
    try {
      const res = await getAllMainCategories(token);
      if (res && res.status >= 200 && res.status < 300) {
        setMainCategories(res?.data?.results);
        console.log("Categories fetched successfully:", res.data);
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch categories. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error || "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchMainCategories();
  }, []);

  const onSubmit = async (item) => {
    setIsLoading2(true);

    const data = {
      sub_category_name: item.subCategoryName,
      main_category: item.mainCategory,
      image: uploadImage || null,
      description: item.description,
    };

    // console.log(data);

    try {
      const res = await addSubCategory(token, data);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Added!",
          text: `${item.subCategoryName} Subcategory has been added successfully.`,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
            navigate("/sub-categories");
        });
      } else if (res && res.status >= 400 && res.status < 500) {
        Swal.fire(
          "Error",
          res?.data?.message || "Invalid input. Please check your data.",
          "error"
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.error || "An unexpected error occurred. Please try again later.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during adding subcategory:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error || "A network or server error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading2(false);
    }
  };

   const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (
        ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(
          file.type
        )
      ) {
        if (file.size < 1 * 1024 * 1024) {
          setImagePreview(URL.createObjectURL(file));
          setUploadImage(file);
          clearErrors("image");
        } else {
          setError("image", {
            type: "manual",
            message: "Image size must be less than 1MB.",
          });
          setUploadImage(null);
          setImagePreview(null)
          setValue("image", null);
        }
      } else {
        setError("image", {
          type: "manual",
          message: "Only JPEG, PNG, JPG, or WEBP files are allowed.",
        });
        setUploadImage(null);
        setImagePreview(null)
        setValue("image", null);
      }
    }
  };

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-lg-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Add Sub Category</h4>
                <Link to="/sub-categories">
                  <img src="/assets/images/kidskart/icon/back-button.png" />
                </Link>
              </div>
              <div className="card-body py-3">
                <div className="basic-form">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      {/* Main Category */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Main Category</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <Select
                          options={mainCategories.map((category) => ({
                            value: category.category_id,
                            label: category.category_name,
                          }))}
                          {...register("mainCategory", {
                            required: "Main Category is required",
                          })}
                          onChange={(selectedOption) => {
                            setValue("mainCategory", selectedOption?.value);
                            clearErrors("mainCategory");
                          }}
                          placeholder="Select Category"
                          isClearable
                          value={mainCategories
                            .map((category) => ({
                              value: category.category_id,
                              label: category.category_name,
                            }))
                            .find(
                              (option) => option.value === watch("mainCategory")
                            )}
                        />
                        {errors.mainCategory && (
                          <p className="error">{errors.mainCategory.message}</p>
                        )}
                      </div>

                      {/* Sub Category Name */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">
                            Sub Category Name
                          </label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter category name"
                          {...register("subCategoryName", {
                            required: "Sub Category is required",
                          })}
                        />
                        {errors.subCategoryName && (
                          <p className="error">
                            {errors.subCategoryName.message}
                          </p>
                        )}
                      </div>

                      {/* Image */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Image</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <input
                          type="file"
                          className="form-control"
                          {...register("image", {
                            required: "Image is required",
                          })}
                          onChange={handleImageChange}
                        />
                        {errors.image && (
                          <p className="error">{errors.image.message}</p>
                        )}
                      </div>
                      <div className="mb-4 col-md-6">
                        {imagePreview && (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="img-thumbnail mt-2"
                            style={{ width: "100px" }}
                          />
                        )}
                      </div>

                      {/* Description */}
                      <div className="mb-4 col-md-12">
                        <label className="form-label">Description</label>
                        <textarea
                          rows="3"
                          className="form-control"
                          placeholder="Enter description"
                          {...register("description")}
                        ></textarea>
                        {errors.description && (
                          <p className="error">{errors.description.message}</p>
                        )}
                      </div>
                    </div>

                    {!isLoading2 ? (
                      <div className="col-sm-4">
                        <button type="submit" className="btn btn-primary w-100">
                          ADD SUB CATEGORY
                        </button>
                      </div>
                    ) : (
                      <div className="col-sm-4">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          disabled
                        >
                          ADDING SUB CATEGORY
                        </button>

                        <div
                          className="spinner-border2 text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
